import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { HealthBehavior, HealthConfig } from '../behaviors/HealthBehavior';
import { AbductableBehavior } from '../behaviors/AbductableBehavior';
import { PlatformBehavior } from '../behaviors/PlatformBehavior';
import { EntityTiming } from '../../constants/enums';

// State types by category
export type MovementState = 'idle' | 'walking' | 'running' | 'guarding';
export type ActionState = 'none' | 'attacking';
export type EffectState = 'none' | 'frozen' | 'stunned';
export type DamageState = 'none' | 'light' | 'medium' | 'heavy' | 'critical';
export type FacingState = 'right' | 'left' | 'up' | 'down';
export type AirborneState = 'grounded' | 'jumping' | 'falling' | 'flying';
export type SpecialState = 'none' | 'abducted' | 'falling' | 'death';

export interface UnitStates {
  movement: MovementState;
  action: ActionState;
  effect: EffectState;
  damage: DamageState;
  facing: FacingState;
  airborne: AirborneState;
  special: SpecialState;
}

// Mapping states to animation names
// Animation names MUST be lowercase per project rules
const ANIMATION_NAMES: Record<string, string> = {
  idle: 'standby',
  walking: 'walking',
  running: 'running',
  guarding: 'guarding',
  attacking: 'attacking',
  frozen: 'frozen',
  stunned: 'stunned',
  light: 'damagedlight',
  medium: 'damagedmedium',
  heavy: 'damagedheavy',
  critical: 'critical',
  abducted: 'abducted',
  death: 'death',
  jumping: 'jumping',
  falling: 'falling',
  flying: 'flying'
};

export interface UnitConfig extends EntityConfig {
  hp?: number;
  maxHp?: number;
  behaviors?: {
    health?: HealthConfig;
    [key: string]: unknown;
  };
}

/**
 * Unit - Base class for entities with HP that can move
 *
 * Used for: Cow, Soldier, Bat, JetpackHuman, etc.
 *
 * Features:
 * - Always has HealthBehavior
 * - Can have movement behaviors (Walker, Flyer)
 * - Can be abductable
 * - Can shoot
 */
export class Unit extends Entity {
  public health: HealthBehavior;

  // Multi-category state system
  public states: UnitStates = {
    movement: 'idle',
    action: 'none',
    effect: 'none',
    damage: 'none',
    facing: 'right',
    airborne: 'grounded',
    special: 'none'
  };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: UnitConfig = {}
  ) {
    super(scene, x, y, texture, config);

    // Units ALWAYS have health
    this.health = this.addBehavior(new HealthBehavior(this, {
      hp: config.hp ?? 100,
      maxHp: config.maxHp ?? config.hp ?? 100,
      ...config.behaviors?.health
    }));

    // Start with initial animation (idle/standby)
    // Defer to next frame to ensure animation system is ready
    this.scene.time.delayedCall(0, () => {
      this.updateAnimation();
    });
  }

  // ========================================
  // State Setters
  // ========================================

  /**
   * Set movement state (idle, walking, running, guarding)
   */
  setMovement(state: MovementState): void {
    if (!this.scene) {
      console.warn(`[Unit] setMovement(${state}) called on destroyed entity: ${this.config?.type}`);
      return;
    }
    if (this.states.special === 'death') return;
    this.states.movement = state;
    this.updateAnimation();
    this.emit('movementChanged', state);
  }

  /**
   * Set action state (none, attacking)
   */
  setAction(state: ActionState): void {
    if (!this.scene) {
      console.warn(`[Unit] setAction(${state}) called on destroyed entity: ${this.config?.type}`);
      return;
    }
    if (this.states.special === 'death') return;
    this.states.action = state;
    this.updateAnimation();
    this.emit('actionChanged', state);
  }

  /**
   * Set effect state (none, frozen, stunned)
   */
  setEffect(state: EffectState): void {
    if (!this.scene) {
      console.warn(`[Unit] setEffect(${state}) called on destroyed entity: ${this.config?.type}`);
      return;
    }
    if (this.states.special === 'death') return;
    this.states.effect = state;
    this.updateAnimation();
    this.emit('effectChanged', state);
  }

  /**
   * Set damage state (none, light, medium, heavy, critical)
   * Called automatically by HealthBehavior
   */
  setDamage(state: DamageState): void {
    if (this.states.special === 'death') return;
    this.states.damage = state;
    this.updateAnimation();
    this.emit('damageChanged', state);
  }

  /**
   * Set facing direction (right, left, up, down)
   * Also handles flipX for sprites
   */
  setFacing(state: FacingState): void {
    if (this.states.special === 'death') return;
    this.states.facing = state;
    // Facing uses flipX by default
    this.flipX = (state === 'left');
    this.updateAnimation();
    this.emit('facingChanged', state);
  }

  /**
   * Set airborne state (grounded, jumping, falling)
   */
  setAirborne(state: AirborneState): void {
    if (this.states.special === 'death') return;
    this.states.airborne = state;
    this.updateAnimation();
    this.emit('airborneChanged', state);
  }

  /**
   * Set special state (none, abducted, death)
   * Death is irreversible
   */
  setSpecial(state: SpecialState): void {
    // Death is irreversible
    if (this.states.special === 'death' && state !== 'death') return;
    this.states.special = state;
    this.updateAnimation();
    this.emit('specialChanged', state);
  }

  // ========================================
  // Animation Resolution
  // ========================================

  /**
   * Resolve which animation to play based on current states
   * Uses priority-based fallback system
   */
  protected selectAnimationByState(): string {
    const { movement, action, effect, damage, airborne, special } = this.states;

    // 1. Special states have maximum priority (lowercase per project rules)
    if (special === 'death') return 'death';
    if (special === 'abducted') return 'abducted';
    if (special === 'falling') return 'falling';

    // 2. Effect states
    if (effect === 'frozen') return 'frozen';
    if (effect === 'stunned') return 'stunned';

    // 3. Airborne states (if not grounded)
    if (airborne === 'flying') {
      const flyAnim = this.tryAnimationWithDamage('flying', damage);
      if (flyAnim) return flyAnim;
    }
    if (airborne === 'jumping') {
      const jumpAnim = this.tryAnimationWithDamage('jumping', damage);
      if (jumpAnim) return jumpAnim;
    }
    if (airborne === 'falling') {
      const fallAnim = this.tryAnimationWithDamage('falling', damage);
      if (fallAnim) return fallAnim;
    }

    // 4. Try combinations with damage
    if (damage !== 'none') {
      const baseAnim = ANIMATION_NAMES[movement] || 'standby';
      const combo = baseAnim + damage;  // e.g., 'standbylight' - lowercase per project rules
      if (this.hasAnimation(combo)) return combo;

      // Try generic damage animation
      const damageAnim = ANIMATION_NAMES[damage];
      if (damageAnim && this.hasAnimation(damageAnim)) return damageAnim;
    }

    // 5. Action state
    if (action === 'attacking') return 'attacking';

    // 6. Base movement
    return ANIMATION_NAMES[movement] || 'standby';
  }

  /**
   * Try to find animation with damage variant
   * Returns the animation name (without prefix) if found
   */
  private tryAnimationWithDamage(base: string, damage: DamageState): string | null {
    if (damage !== 'none') {
      const combo = base + damage;  // e.g., 'flyinglight' - lowercase per project rules
      if (this.hasAnimation(combo)) return combo;
    }
    if (this.hasAnimation(base)) return base;
    return null;
  }

  /**
   * Get full animation key with texture prefix
   * Animations are named: {texture}_{animationname} (e.g., cow1_walking) - lowercase per project rules
   * Uses case-insensitive search to handle manifest/atlas case mismatches
   */
  protected getAnimationKey(animName: string): string {
    const baseKey = `${this.texture.key}_${animName}`;
    // First try exact match
    if (this.scene.anims.exists(baseKey)) {
      return baseKey;
    }
    // Try case-insensitive search
    const lowerKey = baseKey.toLowerCase();
    const animKeys = this.scene.anims.anims.keys();
    for (const key of animKeys) {
      if (key.toLowerCase() === lowerKey) {
        return key;
      }
    }
    // Return original key if not found (will fail gracefully)
    return baseKey;
  }

  /**
   * Check if animation exists with texture prefix
   * Uses case-insensitive search
   */
  protected hasAnimation(animName: string): boolean {
    const baseKey = `${this.texture.key}_${animName}`;
    // First try exact match
    if (this.scene.anims.exists(baseKey)) {
      return true;
    }
    // Try case-insensitive search
    const lowerKey = baseKey.toLowerCase();
    const animKeys = this.scene.anims.anims.keys();
    for (const key of animKeys) {
      if (key.toLowerCase() === lowerKey) {
        return true;
      }
    }
    return false;
  }

  /**
   * Update sprite animation based on current states
   */
  protected updateAnimation(): void {
    // Guard: skip if entity was destroyed
    if (!this.scene) {
      console.warn(`[Unit] updateAnimation() called on destroyed entity: ${this.config?.type}`);
      return;
    }

    const animName = this.selectAnimationByState();
    const animKey = this.getAnimationKey(animName);

    if (this.scene.anims.exists(animKey)) {
      if (this.anims.currentAnim?.key !== animKey) {
        this.play(animKey);
      }
    }
  }

  // ========================================
  // State Queries
  // ========================================

  /** Check if unit is dead */
  isDead(): boolean { return this.states.special === 'death'; }

  /** Check if unit is being abducted */
  isAbducted(): boolean { return this.states.special === 'abducted'; }

  /** Check if unit is frozen */
  isFrozen(): boolean { return this.states.effect === 'frozen'; }

  /** Check if unit is stunned */
  isStunned(): boolean { return this.states.effect === 'stunned'; }

  /** Check if unit is attacking */
  isAttacking(): boolean { return this.states.action === 'attacking'; }

  /** Check if unit is fleeing/running */
  isRunning(): boolean { return this.states.movement === 'running'; }

  /** Get current damage level */
  getDamageLevel(): DamageState { return this.states.damage; }

  // ========================================
  // Reset (for pooling)
  // ========================================

  /**
   * Reset all states to default values
   * Called when entity is reused from pool
   */
  resetAllStates(): void {
    this.states = {
      movement: 'idle',
      action: 'none',
      effect: 'none',
      damage: 'none',
      facing: 'right',
      airborne: 'grounded',
      special: 'none'
    };
    this.flipX = false;
  }

  /**
   * Override Entity.reset to include state reset
   */
  override reset(config: Record<string, unknown> = {}): void {
    super.reset(config);
    this.resetAllStates();
    this.updateAnimation();
  }

  // Shortcuts for health
  get hp(): number {
    return this.health.hp;
  }

  get maxHp(): number {
    return this.health.maxHp;
  }

  /**
   * Take damage (shortcut to health behavior)
   */
  takeDamage(amount: number): boolean {
    // Si estÃ¡ siendo abducido, ignorar el daÃ±o para evitar bugs de estado/posiciÃ³n
    const abductState = this.states.special;
    if (abductState === 'abducted') {
      return false;
    }
    return this.health.takeDamage(amount);
  }

  /**
   * Heal (shortcut to health behavior)
   */
  heal(amount: number): void {
    this.health.heal(amount);
  }

  /**
   * Check if alive (shortcut)
   */
  isAlive(): boolean {
    return this.health.isAlive();
  }

  /**
   * Override in subclasses for custom damage effects
   */
  onDamage(amount: number): void {
    // Flash red effect
    this.setTint(0xff0000);
    this.scene.time.delayedCall(EntityTiming.DamageFlashDuration, () => {
      this.clearTint();
    });
  }

  /**
   * Override in subclasses for custom death effects
   */
  onDeath(): void {
    // Set death state (irreversible) - this triggers the Death animation
    this.setSpecial('death');

    // Disable all behaviors (stop walking, shooting, etc.)
    this.setAllBehaviorsEnabled(false);

    // No Mantener abducción activa si está permitido (osea el cadáver aún se puede abducir)
    const abductable = this.getBehavior(AbductableBehavior as unknown as new (...args: unknown[]) => AbductableBehavior);
    if (abductable?.canAbductWhileDying) {
      abductable.enable();
      const platform = this.getBehavior(PlatformBehavior as unknown as new (...args: unknown[]) => PlatformBehavior);
      platform?.enable();
    }

    // Stop physics movement but keep entity visible for animation
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.setVelocity(0, 0);
      body.setImmovable(true);
      // Evita que el cadáver siga cayendo fuera del mundo
      body.setAllowGravity(false);
    }
  }

  /**
   * Override in subclasses for death handling (animations, drops, etc.)
   */
  handleDeath(): void {
    const deathAnimKey = this.getAnimationKey('death');

    if (this.scene.anims.exists(deathAnimKey)) {
      // Wait for death animation to complete
      this.once('animationcomplete', (anim: Phaser.Animations.Animation) => {
        if (anim.key === deathAnimKey) {
          this.finalizeDeath();
        }
      });

      // Fallback timeout in case animation doesn't fire complete
      this.scene.time.delayedCall(2000, () => {
        if (this.active) {
          this.finalizeDeath();
        }
      });
    } else {
      // No death animation - use delay and fade out
      this.scene.tweens.add({
        targets: this,
        alpha: 0,
        duration: EntityTiming.DeathDelay,
        onComplete: () => this.finalizeDeath()
      });
    }
  }

  /**
   * Final cleanup after death animation
   */
  protected finalizeDeath(): void {
    // Don't finalize if being abducted - let TractorBeamBehavior handle destruction
    const abductable = this.behaviors.find(b => 'isBeingAbducted' in b) as { isBeingAbducted?: boolean } | undefined;
    if (abductable?.isBeingAbducted) {
      return;
    }

    this.setActive(false);
    this.setVisible(false);
    this.destroy();
  }
}
